<template>
  <v-dialog
    v-model="show"
    :persistent="!cancelable"
    :max-width="maxWidth"
    @click:outside="onClickOutside()"
  >
    <v-card v-if="show">
      <v-card-title v-if="title" class="headline">{{title}}</v-card-title>
      <v-card-text class="pb-0">
        <v-container>
          <v-row>
            <v-col cols="12" v-if="content">
              {{content}}
            </v-col>
            <v-col cols="12">
              <v-form ref="form" @submit.prevent="">
                <v-text-field
                  v-if="!multiline"
                  v-model="input"
                  :placeholder="placeholder"
                  :label="label"
                  :rules="rules"
                  :hint="hint"
                  :error-messages.sync="errorMessage"
                  @keyup.enter="submit()"
                  @update:error="error = $event"
                >
                </v-text-field>
                <v-textarea
                  v-if="multiline"
                  v-model="input"
                  :placeholder="placeholder"
                  :label="label"
                  :rules="rules"
                  :hint="hint"
                  :error-messages.sync="errorMessage"
                  @keyup.ctrl.enter="submit()"
                  @update:error="error = $event"
                ></v-textarea>
              </v-form>
            </v-col>
            <v-col cols="12" v-if="appendContent">
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn v-for="(btn, i) in buttons" :key="i" @click="buttonClick(btn, i)">
          {{btn.title}}
        </v-btn>
        <v-btn color="primary" @click="submit()" :disabled="error" :loading="loading">{{okBtnContent}}</v-btn>
        <v-btn v-if="cancelable" color="secondary" @click="close()">{{cancelBtnContent}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// example:
//
// let result = await this.prompt({
//   label: 'please input message id',
//   default: msg.next ? msg.next : '',
//   hint: '<scene id>/<message id> | <message id>'
// })
// if(result.type === 'input-value'){
//   if(result.input){
//     console.log(result.input)
//   }else{
//     console.log(result.type)
//   }
// }
/**
 * @typedef {{
 *  rules: Array<Function>,
 *  title: string,
 *  content: string,
 *  hint: string,
 *  appendContent: string,
 *  label: string,
 *  placeholder: string,
 *  maxWidth: string,
 *  buttons: Array,
 *  multiline: Boolean,
 *  cancelable: Boolean,
 *  autoClose: Boolean,
 *  okBtnContent: string,
 *  cancelBtnContent: string,
 *  onButtonClick: Function
 * }} PromptOptions
 */
/**
 * @type {PromptOptions}
 */
export const defaultOpts = {
  rules: [],
  default: '',
  title: '',
  content: '',
  hint: '',
  appendContent: '',
  label: '',
  placeholder: '',
  maxWidth: '500px',
  buttons: [],
  multiline: false,
  cancelable: true,
  autoClose: true,
  okBtnContent: 'OK',
  cancelBtnContent: 'CANCEL'
}
export default {
  props: {
  },
  watch: {
    show(show){
    },
    input(input){
      this.$emit('input-update', input)
    }
  },
  data(){
    return {
      error: false,
      errorMessage: '',
      show: this.value,
      maxWidth: '500px',

      rules: [],
      default: '',
      title: '',
      content: '',
      hint: '',
      appendContent: '',
      label: '',
      placeholder: '',
      buttons: [],
      multiline: false,
      /**
       * 是否可以取消該次的 prompt
       */
      cancelable: true,
      /**
       * submit 後自動關閉
       */
      autoClose: true,
      okBtnContent: 'OK',
      cancelBtnContent: 'CANCEL',

      input: '',
      loading: false
    }
  },
  methods: {
    /**
     * @param {PromptOptions} opts
     */
    prompt(opts = {}){
      return new Promise((resolve)=>{
        if(this.show){
          throw new Error(`prompt already started`)
        }
        Object.assign(this, defaultOpts, opts)
        this.show = true
        this.error = false
        this.errorMessage = []
        this.input = this.default
        this.loading = false
        let onInput = ()=>{
          this.error = false
          this.errorMessage = []
        }
        let cb = (data)=>{
          if(data){
            if(data.type === 'button-click' || (!this.autoClose && data.type === 'input-value')){
              if(typeof opts.onButtonClick === 'function'){
                opts.onButtonClick(data, {
                  close: ()=>{
                    this.close()
                    resolve(data)
                  },
                  error: (message)=>{
                    this.error = true
                    this.errorMessage = message
                    this.$off('input-update', onInput)
                    this.$once('input-update', onInput)
                  },
                  validate: ()=>{
                    this.$refs.form.validate()
                  },
                  lock: this.lock.bind(this),
                  unlock: this.unlock.bind(this)
                })
              }
            }else{
              if(this.autoClose){
                this.$off('button-click', cb)
                this.$off('input-value', cb)
                this.$off('cancel', cb)
                this.show = false
                resolve(data)
              }else{
                if(data.type === 'cancel'){
                  this.$off('button-click', cb)
                  this.$off('input-value', cb)
                  this.$off('cancel', cb)
                  resolve(data)
                }
              }
            }
          }
        }
        this.$on('button-click', cb)
        this.$once('input-value', cb)
        this.$once('cancel', cb)
      })
    },
    lock(){
      this.cancelable = false
      this.loading = true
    },
    unlock(){
      this.cancelable = true
      this.loading = false
    },
    /**
     * 當額外的按鈕被點擊
     */
    buttonClick(btn, i){
      this.$emit('button-click', {
        type: 'button-click',
        index: i,
        button: btn,
        input: this.input
      })
    },
    submit(){
      if(this.$refs.form.validate()){
        this.$emit('input-value', {
          type: 'input-value',
          input: this.input.trim()
        })
      }else{
        this.error = true
      }
    },
    onClickOutside(){
      if(this.cancelable){
        this.close()
      }
    },
    close(){
      this.$emit('cancel', {
        type: 'cancel',
        cancel: true
      })
      this.unlock()
      this.show = false
    }
  }
}
</script>

<style>

</style>
